import type {
  LogWithRelations,
  CreateLogDto,
  UpdateLogDto,
  LogListQuery,
} from "@repo/share-log-interfaces";
import type { StateStatus, StoreDeps } from "../types";
import type { ImmerSet } from "../helpers/create-store";
import { createStore } from "../helpers/create-store";
import { withFetchStatus } from "../helpers/with-fetch-status";
import { createReset } from "../helpers/create-reset";

const DEFAULT_LIMIT = 20;

interface LogState {
  logs: LogWithRelations[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
  status: StateStatus;
  filter: LogListQuery;
}

interface LogActions {
  fetchLogs: (workspaceId: number, query?: LogListQuery) => Promise<void>;
  loadMore: (workspaceId: number) => Promise<void>;
  createLog: (
    workspaceId: number,
    dto: CreateLogDto,
  ) => Promise<LogWithRelations>;
  updateLog: (
    workspaceId: number,
    logId: number,
    dto: UpdateLogDto,
  ) => Promise<LogWithRelations>;
  deleteLog: (workspaceId: number, logId: number) => Promise<void>;
  fetchLog: (
    workspaceId: number,
    logId: number,
  ) => Promise<LogWithRelations | null>;
  setFilter: (filter: LogListQuery) => void;
  reset: () => void;
}

export type LogStore = LogState & LogActions;

const initialState: LogState = {
  logs: [],
  total: 0,
  page: 1,
  limit: DEFAULT_LIMIT,
  hasMore: false,
  status: "idle",
  filter: {},
};

function sortedInsert(
  logs: LogWithRelations[],
  log: LogWithRelations,
): LogWithRelations[] {
  const newDate = new Date(log.date).getTime();
  const insertIndex = logs.findIndex(
    (l) =>
      new Date(l.date).getTime() < newDate ||
      (new Date(l.date).getTime() === newDate && l.id < log.id),
  );
  if (insertIndex === -1) {
    logs.push(log);
  } else {
    logs.splice(insertIndex, 0, log);
  }
  return logs;
}

export function createLogStore(deps: StoreDeps) {
  return createStore<LogStore>("LogStore", (set, get) => ({
    ...initialState,

    fetchLogs: (workspaceId, query?) =>
      withFetchStatus(
        (s) => set((state) => { state.status = s; }),
        deps.logger,
        async () => {
          set((state) => { state.filter = query ?? {}; });
          const result = await deps.api.log.list(workspaceId, {
            ...query,
            page: 1,
            limit: DEFAULT_LIMIT,
          });
          set((state) => {
            state.logs = result.logs;
            state.total = result.total;
            state.page = result.page;
            state.limit = result.limit;
            state.hasMore = result.hasMore;
          });
        },
      ),

    loadMore: async (workspaceId) => {
      const { hasMore, status, page, filter } = get();
      if (!hasMore || status === "loading") return;

      await withFetchStatus(
        (s) => set((state) => { state.status = s; }),
        deps.logger,
        async () => {
          const nextPage = page + 1;
          const result = await deps.api.log.list(workspaceId, {
            ...filter,
            page: nextPage,
            limit: DEFAULT_LIMIT,
          });
          set((state) => {
            state.logs = [...state.logs, ...result.logs];
            state.total = result.total;
            state.page = result.page;
            state.limit = result.limit;
            state.hasMore = result.hasMore;
          });
        },
      );
    },

    createLog: async (workspaceId, dto) => {
      const log = await deps.api.log.create(workspaceId, dto);
      set((state) => {
        sortedInsert(state.logs, log);
        state.total += 1;
      });
      return log;
    },

    updateLog: async (workspaceId, logId, dto) => {
      const log = await deps.api.log.update(workspaceId, logId, dto);
      set((state) => {
        state.logs = state.logs.filter((l) => l.id !== logId);
        sortedInsert(state.logs, log);
      });
      return log;
    },

    deleteLog: async (workspaceId, logId) => {
      await deps.api.log.remove(workspaceId, logId);
      set((state) => {
        state.logs = state.logs.filter((l) => l.id !== logId);
        state.total -= 1;
      });
    },

    fetchLog: async (workspaceId, logId) => {
      try {
        return await deps.api.log.get(workspaceId, logId);
      } catch {
        return null;
      }
    },

    setFilter: (filter) => {
      set((state) => { state.filter = filter; });
    },

    reset: createReset(set as ImmerSet<LogState>, initialState),
  }));
}
